import { cn } from '../lib/cn'

import iconDark from './assets/icon-dark.svg'
import icon from './assets/icon.svg'
import logoDark from './assets/logo-dark.svg'
import logo from './assets/logo.svg'

const ASSETS = {
  mark: { light: icon, dark: iconDark },
  wordmark: { light: logo, dark: logoDark },
} as const

export interface MilliswiftLogoProps {
  /**
   * `wordmark` — the full lockup: the `m` glyph beside the "milliswift"
   *             lettering, which is what every header should use.
   * `mark`     — the glyph alone, square, for the collapsed sidebar.
   *
   * The shell picks between them from its own collapsed state; there is no
   * viewport-based auto mode, because the sidebar collapses on a button, not
   * on a breakpoint.
   */
  variant?: 'wordmark' | 'mark'
  /** The surface the logo sits on. The artwork is dark; `dark` lifts it. */
  tone?: 'light' | 'dark'
  /** Rendered height in px; width follows the intrinsic aspect ratio. */
  height?: number
  className?: string
  alt?: string
}

/**
 * The milliswift logo.
 *
 * Both variants are vector, built by `npm run logo` (scripts/build-logo.py)
 * from the upstream artwork in assets/brand-source/. That build unifies the
 * artwork height across the glyph and the wordmark and unifies the spacing —
 * every outer margin and the gap between the two halves are one value — so the
 * lockup reads as a single mark rather than two files dropped side by side.
 *
 * Because the files are vector there is no size ladder to pick from, which is
 * why this is a plain `<img>`: no `srcSet`, no `<picture>`, no WebP sibling, and
 * no raster derivatives in the bundle at all. Each SVG declares its own
 * `width`/`height`, so the browser reserves the correct box before the file
 * loads and the header cannot shift — which is what the `width`/`height`
 * attributes on the old raster derivatives were for.
 *
 * Rendered via `<img>` rather than inlined, so the artwork stays a separate
 * cacheable file and inherits no page styles.
 */
export function MilliswiftLogo({
  variant = 'wordmark',
  tone = 'light',
  height = 32,
  className,
  alt = 'milliswift',
}: MilliswiftLogoProps) {
  const src = ASSETS[variant][tone]
  const square = variant === 'mark'

  return (
    <img
      src={src}
      alt={alt}
      // Width is pinned only for the square glyph; the lockup lets its own
      // intrinsic ratio drive it, so the two halves cannot drift apart.
      style={square ? { height, width: height } : { height }}
      className={cn('block w-auto shrink-0 select-none', className)}
    />
  )
}
